import { Spine } from "@esotericsoftware/spine-pixi-v7";
import * as PIXI from "pixi.js";
import { BACKGROUND_OFFSET_CONFIG } from "./BackgroundAnimationController";
import SpineAtlasUploader from "../../../utils/SpineAtlasUploader";

/**
 * CharacterAnimationController
 *
 * Controls the character spine animation that overlays the background.
 * Uses the same scaling logic as BackgroundAnimationController so both
 * animations align pixel-perfectly.
 *
 * The spine asset name is configured via constructor — defaults to "character".
 */
class CharacterAnimationController {
  constructor(
    container,
    symbolWidth,
    symbolHeight,
    symbolScale,
    dimensions,
    device,
    orientation,
    spineName = "character",
  ) {
    this.container = container;
    this.spineData = {
      skeleton: `${spineName}-skeleton`,
      atlas: `${spineName}-atlas`,
    };
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
    this.symbolScale = symbolScale;
    this.dimensions = dimensions;
    this.device = device;
    this.orientation = orientation;

    this.characterSpine = null;
    this.isPlaying = false;
    this._currentIdleIndex = 0;
    this._idleLoopCount = 0;
    this._idleListener = null;
    this._animListener = null;
    this._pendingAnimation = null;
    this._isPlayingQueued = false; // true while a queued animation (go_go_go) is actively playing

    this.initializeSpine();
  }

  initializeSpine() {
    try {
      this.characterSpine = this.createSpine();
      if (!this.characterSpine) return;

      if (!this.characterSpine.skeleton || !this.characterSpine.state) return;

      // Position at origin — BackgroundAnimation container handles global positioning
      this.characterSpine.x = 0;
      this.characterSpine.y = 0;
      this.characterSpine.zIndex = 5; // Above background spine (-10) but below UI
      this.characterSpine.alpha = 0.001; // Nearly invisible during warmup
      this.characterSpine.eventMode = "none"; // Don't intercept pointer events (MiniPaytable sits above)

      this.container.eventMode = "none"; // Container also non-interactive
      this.container.addChild(this.characterSpine);

      // Apply same scale and position as background
      this.applyScale();

      // Warm up: play idle_0 frame 0 to initialize GPU textures and skeleton
      this.warmUp();

      // Force-upload character atlas textures to GPU
      SpineAtlasUploader.upload("character");

      // Show on next frame — warmup pose rendered, no flash
      requestAnimationFrame(() => {
        if (this.characterSpine) {
          this.characterSpine.alpha = 1;
          this.playIdle();
        }
      });
    } catch (error) {
      // Silent fail
    }
  }

  /**
   * Warm up the spine — render one frame at alpha 0.001 to prevent first-frame flash
   */
  warmUp() {
    if (!this.characterSpine || !this.characterSpine.state) return;

    try {
      this.characterSpine.state.setAnimation(0, "idle_0", false);
      this.characterSpine.state.update(0.016);
      this.characterSpine.skeleton.updateWorldTransform();
      this.characterSpine.alpha = 0.001;
    } catch (error) {
      // Silent fail
    }
  }

  createSpine() {
    try {
      const assets = PIXI.Assets.cache;
      if (
        !assets.has(this.spineData.skeleton) ||
        !assets.has(this.spineData.atlas)
      ) {
        return null;
      }

      return Spine.from({
        skeleton: this.spineData.skeleton,
        atlas: this.spineData.atlas,
      });
    } catch (error) {
      return null;
    }
  }

  /**
   * Apply the same scaling AND positioning as BackgroundAnimationController.applyResponsiveBackgroundScaling
   * so the character matches the background exactly
   */
  applyScale() {
    if (!this.characterSpine || !this.dimensions) return;

    const { width, height } = this.dimensions;

    // Match background scaling: min(1, symbolScale)
    const backgroundScale = Math.min(1, this.symbolScale);

    const centerX = width / 2;

    // Follow background offset config (same as background controller)
    const deviceConfig = BACKGROUND_OFFSET_CONFIG[this.device];
    const offsetMultiplier =
      typeof deviceConfig === "number"
        ? deviceConfig
        : (deviceConfig?.[this.orientation] ?? 0);
    const backgroundOffset = this.symbolHeight * offsetMultiplier;
    const positionY = height / 2 + backgroundOffset;

    this.characterSpine.scale.set(backgroundScale);
    this.characterSpine.position.set(centerX, positionY);
  }

  /**
   * Finish at the nearest "center" frame, then hide and fire callback.
   *
   * The character swings left↔right, so the center pose (= transition's first
   * frame) appears TWICE per loop: at time 0 and at duration/2.
   * We fire at whichever center comes next — max wait is half a loop.
   *
   * @param {Function} onComplete - called after idle reaches center and character hides
   */
  finishIdleAndHide(onComplete) {
    if (!this.characterSpine || !this.characterSpine.state) {
      if (onComplete) onComplete();
      return;
    }

    try {
      // Clean up all existing listeners to prevent conflicts
      if (this._idleListener) {
        this.characterSpine.state.removeListener(this._idleListener);
        this._idleListener = null;
      }
      if (this._animListener) {
        this.characterSpine.state.removeListener(this._animListener);
        this._animListener = null;
      }

      // Clear any pending queued animation — transition takes priority
      this._pendingAnimation = null;
      this._isPlayingQueued = false;

      const currentTrack = this.characterSpine.state.tracks[0];
      const currentAnim = currentTrack?.animation?.name;

      if (!currentTrack) {
        this.hide();
        if (onComplete) onComplete();
        return;
      }

      // If not in idle_0, force-jump to it (starts at center = time 0).
      // Then wait for the midpoint so the character has a half-swing to settle.
      if (currentAnim !== "idle_0") {
        this.characterSpine.state.setAnimation(0, "idle_0", false);
      } else {
        currentTrack.loop = false;
      }

      // Determine the nearest center frame and wait for it
      const track = this.characterSpine.state.tracks[0];
      const duration = track.animation.duration;
      const halfDuration = duration / 2;
      const currentTime = track.trackTime % duration;

      const fireTransition = () => {
        if (onComplete) onComplete();
        requestAnimationFrame(() => this.hide());
      };

      if (currentTime < halfDuration) {
        // Next center is at the midpoint — poll each frame for it
        this._midpointCheck = () => {
          const t = this.characterSpine?.state?.tracks?.[0];
          if (!t || t.trackTime >= halfDuration) {
            PIXI.Ticker.shared.remove(this._midpointCheck);
            this._midpointCheck = null;
            fireTransition();
          }
        };
        PIXI.Ticker.shared.add(this._midpointCheck);
      } else {
        // Past midpoint — wait for the loop to complete (end = next center)
        const listener = {
          complete: (entry) => {
            if (entry.animation.name === "idle_0") {
              this.characterSpine.state.removeListener(listener);
              fireTransition();
            }
          },
        };
        this.characterSpine.state.addListener(listener);
      }
    } catch (error) {
      this.hide();
      if (onComplete) onComplete();
    }
  }

  /**
   * Hide the character instantly — used when feature_transition starts
   * (the transition animation contains the character, so we hide the standalone one)
   */
  hide() {
    if (!this.characterSpine) return;
    this.characterSpine.visible = false;
    // Clean up midpoint ticker check if active
    if (this._midpointCheck) {
      PIXI.Ticker.shared.remove(this._midpointCheck);
      this._midpointCheck = null;
    }
    // Pause the animation so it doesn't tick in the background
    if (this.characterSpine.state) {
      this.characterSpine.state.clearTracks();
    }
  }

  /**
   * Show the character and resume idle — used when feature_transition ends.
   * Called synchronously from the spine ticker callback, so the animation
   * is applied before the next render frame — no stale-pose artifact.
   */
  show() {
    if (!this.characterSpine) return;
    this.characterSpine.visible = true;
    this.playIdle();
  }

  /**
   * Play alternating idle: idle_0 × 2 loops → idle_1 × 1 loop → repeat.
   * Checks for queued animations (go_go_go) at idle_0 completion boundaries.
   */
  playIdle() {
    if (!this.characterSpine || !this.characterSpine.state) return;

    // Clean up previous idle listener
    if (this._idleListener) {
      this.characterSpine.state.removeListener(this._idleListener);
      this._idleListener = null;
    }

    const idleName = this._currentIdleIndex === 0 ? "idle_0" : "idle_1";
    const maxLoops = this._currentIdleIndex === 0 ? 2 : 1;

    try {
      this.characterSpine.state.setAnimation(0, idleName, false);

      this._idleListener = {
        complete: (entry) => {
          if (entry.animation.name !== idleName) return;

          this._idleLoopCount++;

          if (this._idleLoopCount < maxLoops) {
            // Play same idle again
            this.characterSpine.state.setAnimation(0, idleName, false);
            return;
          }

          // Reached max loops — check for queued animation at ANY idle boundary
          this._idleLoopCount = 0;

          if (this._pendingAnimation) {
            const pending = this._pendingAnimation;
            this._pendingAnimation = null;
            this._playQueuedAnimation(pending);
            return;
          }

          // No pending — alternate idles
          if (this._currentIdleIndex === 0) {
            this._currentIdleIndex = 1;
          } else {
            this._currentIdleIndex = 0;
          }

          this.playIdle();
        },
      };
      this.characterSpine.state.addListener(this._idleListener);
    } catch (error) {
      // Silent fail
    }
  }

  /**
   * Queue an animation to play after idle_0 finishes (never interrupts).
   * Used for go_go_go idle-timeout animation.
   * @param {string} animName - Animation name to queue
   */
  queueAnimation(animName) {
    // Don't queue if already pending or go_go_go is actively playing
    if (this._pendingAnimation || this._isPlayingQueued) return;
    this._pendingAnimation = animName;
  }

  /**
   * Cancel a pending queued animation without interrupting one that's currently playing.
   * Called when user starts spinning — no need for go_go_go anymore.
   */
  cancelPending() {
    this._pendingAnimation = null;
  }

  /**
   * Internal: play a queued animation, then resume idle cycle from idle_0
   */
  _playQueuedAnimation(animName) {
    if (!this.characterSpine || !this.characterSpine.state) return;

    // Clean up idle listener
    if (this._idleListener) {
      this.characterSpine.state.removeListener(this._idleListener);
      this._idleListener = null;
    }
    if (this._animListener) {
      this.characterSpine.state.removeListener(this._animListener);
      this._animListener = null;
    }

    try {
      this.isPlaying = true;
      this._isPlayingQueued = true;
      this.characterSpine.state.setAnimation(0, animName, false);

      this._animListener = {
        complete: (entry) => {
          if (entry.animation.name === animName) {
            this.isPlaying = false;
            this._isPlayingQueued = false;
            this.characterSpine.state.removeListener(this._animListener);
            this._animListener = null;
            // Resume from idle_0
            this._currentIdleIndex = 0;
            this._idleLoopCount = 0;
            this.playIdle();
          }
        },
      };
      this.characterSpine.state.addListener(this._animListener);
    } catch (error) {
      this.isPlaying = false;
    }
  }

  /**
   * Play a specific animation immediately (interrupts current idle).
   * Used for win reactions (yes, angry). Resumes idle after completion.
   * @param {string} animName - Animation name from the spine
   */
  playAnimation(animName) {
    if (!this.characterSpine || !this.characterSpine.state) return;

    try {
      const animations = this.getAvailableAnimations();
      if (!animations.includes(animName)) return;

      // Remove all listeners to prevent conflicts
      if (this._animListener) {
        this.characterSpine.state.removeListener(this._animListener);
        this._animListener = null;
      }
      if (this._idleListener) {
        this.characterSpine.state.removeListener(this._idleListener);
        this._idleListener = null;
      }

      // Clear any pending queued animation and reset queued-playing flag
      // (prevents permanent blocking of future queueAnimation calls
      // when "yes" interrupts a running go_go_go)
      this._pendingAnimation = null;
      this._isPlayingQueued = false;

      this.isPlaying = true;
      this.characterSpine.state.setAnimation(0, animName, false);

      // Track completion — resume idle alternation after one-shot
      this._animListener = {
        complete: (entry) => {
          if (entry.animation.name === animName) {
            this.isPlaying = false;
            this.characterSpine.state.removeListener(this._animListener);
            this._animListener = null;
            // Resume from idle_0
            this._currentIdleIndex = 0;
            this._idleLoopCount = 0;
            this.playIdle();
          }
        },
      };
      this.characterSpine.state.addListener(this._animListener);
    } catch (error) {
      this.isPlaying = false;
    }
  }

  /**
   * Resize — called from BackgroundAnimation resize handler
   */
  resize(symbolWidth, symbolHeight, symbolScale, dimensions, device, orientation) {
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
    this.symbolScale = symbolScale;
    this.dimensions = dimensions;
    this.device = device;
    this.orientation = orientation;

    this.applyScale();
  }

  getAvailableAnimations() {
    if (!this.characterSpine?.skeleton?.data?.animations) return [];
    return this.characterSpine.skeleton.data.animations.map(
      (anim) => anim.name,
    );
  }

  cleanup() {
    if (this.characterSpine) {
      this.characterSpine.state?.clearListeners();
      this.characterSpine.state?.clearTracks();
      if (
        this.container &&
        this.container.children.includes(this.characterSpine)
      ) {
        this.container.removeChild(this.characterSpine);
      }
      this.characterSpine.destroy();
      this.characterSpine = null;
    }
    this.isPlaying = false;
  }
}

export default CharacterAnimationController;
